import React, { useEffect, useRef } from 'react';
import { HomepageMethod } from './methodsData';
import './MethodDetailModal.css';

interface MethodDetailModalProps {
  method: HomepageMethod | null;
  onClose: () => void;
}

const MethodDetailModal: React.FC<MethodDetailModalProps> = ({ method, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!method) return;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    modalRef.current?.focus();

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [method, onClose]);

  if (!method) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const difficultyColor =
    method.difficulty === 'Einsteiger'
      ? 'green'
      : method.difficulty === 'Mittel'
      ? 'amber'
      : 'purple';

  return (
    <div className="ml-detail-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label={method.name}>
      <div className="ml-detail-modal" ref={modalRef} tabIndex={-1}>
        <div className="ml-detail-header">
          <span className="ml-detail-icon" aria-hidden="true">{method.icon}</span>
          <div className="ml-detail-header-text">
            <h3>{method.name}</h3>
            <span className="ml-detail-category">{method.category}</span>
          </div>
          <button className="ml-detail-close" onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div className="ml-detail-meta">
          <span className="ml-detail-badge">⏱️ {method.duration}</span>
          <span className="ml-detail-badge">👥 {method.participants}</span>
          <span className={`ml-detail-badge ml-detail-badge--${difficultyColor}`}>
            📊 {method.difficulty}
          </span>
        </div>

        <div className="ml-detail-body">
          <div className="ml-detail-section">
            <h4>Beschreibung</h4>
            <p>{method.description}</p>
          </div>

          <div className="ml-detail-section">
            <h4>Nutzen & Einsatzgebiet</h4>
            <p>{method.benefit}</p>
          </div>

          <div className="ml-detail-section">
            <h4>Ablauf</h4>
            <ol className="ml-detail-steps">
              {method.instructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="ml-detail-section">
            <h4>Materialien</h4>
            <ul className="ml-detail-materials">
              {method.materials.map((mat, i) => (
                <li key={i}>{mat}</li>
              ))}
            </ul>
          </div>

          <div className="ml-detail-section">
            <h4>Nachbesprechung</h4>
            <p className="ml-detail-debriefing">{method.debriefing}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodDetailModal;
